import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIcon from "assets/images/drag-icon.svg";
import styled from "styled-components";
import { useField, useForm, useFormState } from "react-final-form";
import { truncate } from "lodash";
import CustomTimePickerInput from "../../../components/CustomTimePickerInput";
import { TextInput } from "react-admin";
import Stack from "../../../components/Stack";
import ActionsMenu from "../../../components/ActionsMenu";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSelectedAddressFromMarker,
  changeSelectedRoute,
  updateRouteTimeData,
} from "../../../redux/navmode.action";
import RemoveRouteLocationButton from "./RemoveRouteLocationButton";
import ChangeLocationButton from "./ChangeLocationButton";
import ManageStudentsButton from "./ManageStudentsButton";
import MoveRouteLocationButton from "./MoveRouteLocationButton";
import { AppState } from "../../../components/layout/Menu";
import CloseIcon from "@material-ui/icons/Close";
import MergeStudentsButton from "./MergeStudentsButton";
import Alert from "@material-ui/lab/Alert";
import { isSchoolLocation } from "../tools";

const ItemWrapper = styled("div")({
  borderRadius: "10px",
  padding: "0px 18px 18px 18px",
  "&:hover": {
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.08)",
  },
});

interface EditableRouteLocationComponentProps {
  school: ISchool;
  busSchedule: IBusSchedule;
  maxStopTime: number;
  routesData: IRoute[];
}

const EditableRouteLocationComponent: FC<
  EditableRouteLocationComponentProps
> = ({ school, busSchedule, maxStopTime, routesData }) => {
  const [selectedLocationOrders, setSelectedLocationOrders] = useState<
    number[]
  >([]);

  const { values } = useFormState();
  const dispatch = useDispatch();
  const form = useForm();

  const {
    address_changes: addressChangesFromMarker,
    route_time_data: routeTimeData,
  } = useSelector((state: AppState) => state.navMode);

  const routeColorField = useField(`routeColor`);
  const routeGeometriesField = useField(`routeGeometries`);
  const routeLocationField = useField(`routeLocations`);

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    // some basic styles to make the items look a bit nicer
    // userSelect: "none",
    margin: "0 0 20px 0",
    border: "1px solid #EAEAEA",

    // change background colour if dragging
    background: isDragging ? "#FBFBFB" : "#FFFFFF",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  // a little function to help us with reordering the result
  const reorder = (list: any, startIndex: any, endIndex: any) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const items = reorder(
      routeLocationField.input.value,
      result.source.index,
      result.destination.index,
    );

    routeLocationField.input.onChange(items);
    routeGeometriesField.input.onChange([]);
  };

  useEffect(() => {
    dispatch(changeSelectedRoute(values));
    dispatch(changeSelectedAddressFromMarker(null));
  }, [values]);

  useEffect(() => {
    if (addressChangesFromMarker) {
      routeLocationField.input.onChange(
        addressChangesFromMarker.routeLocations,
      );
      routeGeometriesField.input.onChange([]);
    }
  }, [addressChangesFromMarker]);

  useEffect(() => {
    if (routeTimeData) {
      form.change("startTime", routeTimeData.startTime);
      form.change("endTime", routeTimeData.endTime);
      form.change(
        "routeLocations",
        routeTimeData.routeLocations.map((loc: any) => ({ ...loc })),
      );

      dispatch(updateRouteTimeData(null));
    }
  }, [routeTimeData]);

  return (
    <Box style={{ marginTop: "20px", paddingTop: "5px" }}>
      {selectedLocationOrders.length > 1 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          style={{
            marginBottom: "10px",
            backgroundColor: "rgb(234, 236, 247)",
            color: "#3f51b5",
            padding: "5px 10px",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            spacing={0}
          >
            <IconButton onClick={() => setSelectedLocationOrders([])}>
              <CloseIcon />
            </IconButton>
            <Typography variant="subtitle1" style={{ fontSize: "11pt" }}>
              {selectedLocationOrders.length} Selected Locations
            </Typography>
          </Stack>

          <MergeStudentsButton
            selectedLocationOrders={selectedLocationOrders}
            setSelectedLocationOrders={setSelectedLocationOrders}
            school={school}
            busSchedule={busSchedule}
            maxStopTime={maxStopTime}
          />
        </Stack>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                background: "#FBFBFB",
                width: "100%",
                borderRadius: "10px",
                maxHeight: "100vh",
                overflowY: "auto",
              }}
            >
              {routeLocationField.input.value.length > 0 && (
                <Box>
                  {routeLocationField.input.value.map(
                    (location: any, index: number) => {
                      if (busSchedule.type === "to_school") {
                        location.order = index + 1;
                      } else {
                        location.order = index;
                      }

                      let locationCodeIndex;

                      if (busSchedule.type === "to_school") {
                        locationCodeIndex = index;
                      } else {
                        locationCodeIndex = index - 1;
                      }

                      // convert ASCII Code into Alphabet
                      // 65 === A
                      const locationCode = String.fromCharCode(
                        65 + locationCodeIndex,
                      );

                      return (
                        <Draggable
                          key={`draggable-${index}`}
                          draggableId={`draggable-${index}`}
                          index={index}
                          isDragDisabled={
                            busSchedule.type === "to_school"
                              ? index ===
                                routeLocationField.input.value.length - 1
                              : index === 0
                          }
                        >
                          {(provided, snapshot) => (
                            <ItemWrapper
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={getItemStyle(
                                snapshot.isDragging,
                                provided.draggableProps.style,
                              )}
                            >
                              {isSchoolLocation(
                                index,
                                routeLocationField.input.value.length,
                                busSchedule.type,
                              ) ? (
                                <Grid container spacing={2}>
                                  <Grid
                                    item
                                    xs={12}
                                    style={{ marginTop: "15px" }}
                                  >
                                    <Typography
                                      style={{
                                        fontSize: "13pt",
                                        fontWeight: 600,
                                        alignSelf: "center",
                                      }}
                                    >
                                      {school.name}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Divider />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Typography
                                      style={{
                                        fontSize: "11pt",
                                        fontWeight: 500,
                                        alignSelf: "center",
                                      }}
                                    >
                                      {location.address}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Typography
                                      style={{
                                        fontSize: "11pt",
                                        fontWeight: 600,
                                        alignSelf: "center",
                                      }}
                                    >
                                      {location.arrivalTime && (
                                        <span>
                                          {busSchedule.type === "to_school"
                                            ? "Arrival Time"
                                            : "Departure Time"}{" "}
                                          : {location.arrivalTime}
                                        </span>
                                      )}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              ) : (
                                <>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      style={{
                                        fontSize: "10pt",
                                        fontWeight: "bold",
                                        borderRadius: "50%",
                                        width: "25px",
                                        height: "25px",
                                        marginTop: "8px",
                                        marginRight: "13px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        backgroundColor:
                                          routeColorField.input.value.colorTwo,
                                        color: "#FFFFFF",
                                      }}
                                    >
                                      {location.order}
                                    </Typography>
                                    <Checkbox
                                      checked={selectedLocationOrders.includes(
                                        location.order,
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedLocationOrders([
                                            ...selectedLocationOrders,
                                            location.order,
                                          ]);
                                        } else {
                                          setSelectedLocationOrders(
                                            selectedLocationOrders.filter(
                                              (i) => i !== location.order,
                                            ),
                                          );
                                        }
                                      }}
                                      style={{
                                        marginTop: "8px",
                                      }}
                                    />
                                  </Box>

                                  <Stack
                                    direction="row"
                                    style={{ flexFlow: "nowrap" }}
                                  >
                                    <Box display="inline-flex">
                                      <div {...provided.dragHandleProps}>
                                        <img
                                          style={{
                                            width: "20px",
                                            height: "45px",
                                            margin: "13px 10px 10px 0px",
                                          }}
                                          src={DragIcon}
                                          alt="Drag"
                                        />
                                      </div>
                                      <Typography
                                        style={{
                                          fontSize: "13pt",
                                          fontWeight: 500,
                                          alignSelf: "center",
                                          maxWidth: "500px",
                                        }}
                                      >
                                        {location.address}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <ActionsMenu
                                        numberOfActions={0}
                                        render={[
                                          <ChangeLocationButton
                                            order={location.order}
                                            disabled={
                                              !school.isCustomPickupAndDropoffLocationAllowedForRouteplan
                                            }
                                            school={school}
                                            busSchedule={busSchedule}
                                            maxStopTime={maxStopTime}
                                          />,
                                          <ManageStudentsButton
                                            order={location.order}
                                            routeType={busSchedule.type}
                                            routesData={routesData}
                                          />,
                                          <MoveRouteLocationButton
                                            order={location.order}
                                            routesData={routesData}
                                            school={school}
                                            busSchedule={busSchedule}
                                            maxStopTime={maxStopTime}
                                          />,
                                          <RemoveRouteLocationButton
                                            order={location.order}
                                            school={school}
                                            busSchedule={busSchedule}
                                            maxStopTime={maxStopTime}
                                          />,
                                        ]}
                                      />
                                    </Box>
                                  </Stack>

                                  <Box>
                                    <CustomTimePickerInput
                                      source={`routeLocations.${index}.arrivalTime`}
                                      label="Arrival Time"
                                    />

                                    <Alert
                                      severity="info"
                                      style={{ marginTop: "10px" }}
                                    >
                                      <b>Arrival Time</b> is approximate and
                                      calculated based on the planned route.
                                    </Alert>
                                  </Box>

                                  <Box>
                                    <TextInput
                                      source={`routeLocations.${index}.remark`}
                                      label="Remark location pick/drop off"
                                    />
                                  </Box>

                                  <Box>
                                    {location.students
                                      .sort((a: any, b: any) => a.id - b.id)
                                      .map((student: any, index: number) => {
                                        student.locationCode = `${locationCode}${
                                          index + 1
                                        }`;

                                        return (
                                          <Chip
                                            key={`student-key-${index}`}
                                            label={`${
                                              student.locationCode
                                            } - ${truncate(
                                              `${student.firstName} ${student.lastName}`,
                                              {
                                                length: 15,
                                              },
                                            )}`}
                                            style={{
                                              margin: 5,
                                              backgroundColor: "#FFFFFF",
                                              color: "#6B6B6B",
                                              border: "1px solid #E3E3E3",
                                              borderRadius: 5,
                                            }}
                                          />
                                        );
                                      })}
                                  </Box>
                                </>
                              )}
                            </ItemWrapper>
                          )}
                        </Draggable>
                      );
                    },
                  )}
                </Box>
              )}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default EditableRouteLocationComponent;
